import React, { useState, useEffect } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import apiClient from '../services/api';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const formatKip = (amount) => {
  if (amount === undefined || amount === null) return '₭0';
  const num = Number(amount);
  if (isNaN(num)) return '₭0';
  if (num >= 1e9) {
    return `₭${parseFloat((num / 1e9).toFixed(2))}B`;
  } else if (num >= 1e6) {
    return `₭${parseFloat((num / 1e6).toFixed(2))}M`;
  } else {
    return `₭${num.toLocaleString()}`;
  }
};

const MODULE_COLORS = {
  toll: '#fa8c16', bridge: '#1890ff', wim: '#f5222d', auction: '#52c41a', topup: '#9254de',
};

const periodDays = { today: 1, week7: 7, '30d': 30, '90d': 90 };

/**
 * Analytics.jsx — Revenue Analytics
 * All data fetched from backend API — no mock/hardcoded values.
 */
const Analytics = ({ lang = 'lo', theme = 'dark' }) => {
  const [period, setPeriod]           = useState('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd]     = useState('');
  const [loading, setLoading]         = useState(true);
  const [trendByModule, setTrend]     = useState([]);
  const [hourlyData, setHourly]       = useState([]);
  const [sourceData, setSource]       = useState([]);
  const [topStations, setTopStations] = useState([]);
  const [apiStatus, setApiStatus]     = useState([]);
  const [kpiData, setKpiData]         = useState(null);

  const i18n = {
    lo: {
      title: 'ວິເຄາະລາຍຮັບ',
      subtitle: 'ການວິເຄາະລາຍຮັບ ແລະ ປະສິດທິພາບລະບົບ',
      revTrend: 'ແນວໂນ້ມລາຍຮັບ',
      moduleComp: 'ປຽບທຽບລາຍຮັບຕາມໂມດູນ',
      systemPerf: 'ສະຖານະລະບົບ',
      systemHealth: 'ສຸຂະພາບລະບົບ',
      txnVolume: 'ປະລິມານທຸລະກຳ',
      totalRevenue: 'ລາຍຮັບລວມ',
      totalTxn: 'ທຸລະກຳທັງໝົດ',
      growthRate: 'ອັດຕາເຕີບໂຕ',
      topSource: 'ໂມດູນສູງສຸດ',
      uptime: 'Uptime ລະບົບ',
      errorRate: 'ອັດຕາຂໍ້ຜິດພາດ',
      avgResponse: 'ເວລາຕອບສະໜອງ',
      activeGates: 'ດ່ານທີ່ໃຊ້ງານ',
      today: 'ວັນນີ້', week7: '7 ວັນ', day30: '30 ວັນ', day90: '90 ວັນ',
      toll: 'ຄ່າທາງດ່ວນ', bridge: 'ຄ່າຂ້າມສະພານ', wim: 'ຄ່າປັບນ້ຳໜັກ', auction: 'ປະມູນປ້າຍ', topup: 'OBU ອັດຕາເຕີມ',
      hourlyDist: 'ການກະຈາຍຊົ່ວໂມງ',
      topPerf: 'ດ່ານທີ່ດີທີ່ສຸດ',
      rank: 'ອັນດັບ', station: 'ດ່ານ', revenue: 'ລາຍຮັບ', change: 'ສ່ວນແບ່ງ',
      customRange: 'ກຳນົດເອງ',
      fromDate: 'ຈາກວັນທີ',
      toDate: 'ຫາວັນທີ',
      apply: 'ນຳໃຊ້',
    },
    th: {
      title: 'วิเคราะห์รายได้',
      subtitle: 'การวิเคราะห์รายได้และประสิทธิภาพระบบ',
      revTrend: 'แนวโน้มรายได้',
      moduleComp: 'เปรียบเทียบรายได้ตามโมดูล',
      systemPerf: 'สถานะระบบ',
      systemHealth: 'สุขภาพระบบ',
      txnVolume: 'ปริมาณธุรกรรม',
      totalRevenue: 'รายได้รวม',
      totalTxn: 'ธุรกรรมทั้งหมด',
      growthRate: 'อัตราเติบโต',
      topSource: 'โมดูลสูงสุด',
      uptime: 'Uptime ระบบ',
      errorRate: 'อัตราข้อผิดพลาด',
      avgResponse: 'เวลาตอบสนอง',
      activeGates: 'ด่านที่ใช้งาน',
      today: 'วันนี้', week7: '7 วัน', day30: '30 วัน', day90: '90 วัน',
      toll: 'ค่าทางด่วน', bridge: 'ค่าข้ามสะพาน', wim: 'ค่าปรับน้ำหนัก', auction: 'ประมูลป้าย', topup: 'อัดเติม OBU',
      hourlyDist: 'การกระจายรายชั่วโมง',
      topPerf: 'ด่านที่ดีที่สุด',
      rank: 'อันดับ', station: 'ด่าน', revenue: 'รายได้', change: 'สัดส่วน',
      customRange: 'กำหนดเอง',
      fromDate: 'จากวันที่',
      toDate: 'ถึงวันที่',
      apply: 'นำไปใช้',
    },
    en: {
      title: 'Revenue Analytics',
      subtitle: 'Revenue analysis and system performance metrics',
      revTrend: 'Revenue Trend',
      moduleComp: 'Revenue by Module',
      systemPerf: 'System Performance',
      systemHealth: 'System Health',
      txnVolume: 'Transaction Volume',
      totalRevenue: 'Total Revenue',
      totalTxn: 'Total Transactions',
      growthRate: 'Growth Rate',
      topSource: 'Top Module',
      uptime: 'System Uptime',
      errorRate: 'Error Rate',
      avgResponse: 'Avg Response',
      activeGates: 'Active Gates',
      today: 'Today', week7: '7 Days', day30: '30 Days', day90: '90 Days',
      toll: 'Toll Fee', bridge: 'Bridge Fee', wim: 'WIM Fine', auction: 'Plate Auction', topup: 'OBU TopUp',
      hourlyDist: 'Hourly Distribution',
      topPerf: 'Top Performing Stations',
      rank: 'Rank', station: 'Station', revenue: 'Revenue', change: 'Share',
      customRange: 'Custom Range',
      fromDate: 'From Date',
      toDate: 'To Date',
      apply: 'Apply',
    },
  };
  const t = (k) => i18n[lang]?.[k] || i18n.en[k] || k;





  const loadAnalyticsData = async (params = {}) => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        apiClient.dashboardGetTrendByModule(params),
        apiClient.dashboardGetHourlyDistribution(params),
        apiClient.dashboardGetRevenueBySource(params),
        apiClient.dashboardGetTopStations({ ...params, limit: 7 }),
        apiClient.settingsGetApiStatus(),
        apiClient.dashboardGetKpi(params),
      ]);
      const val = (r) => r.status === 'fulfilled' ? r.value : null;
      const tr = val(results[0]);    if (tr?.success)       setTrend(Array.isArray(tr.data) ? tr.data : []);
      const hr = val(results[1]);    if (hr?.success)       setHourly(Array.isArray(hr.data) ? hr.data : []);
      const sr = val(results[2]);    if (sr?.success)       setSource(Array.isArray(sr.data) ? sr.data : []);
      const st = val(results[3]);    if (st?.success)       setTopStations(Array.isArray(st.data) ? st.data : []);
      const ar = val(results[4]);    if (ar?.success)       setApiStatus(Array.isArray(ar.data) ? ar.data : []);
      const kr = val(results[5]);    if (kr?.success)       setKpiData(kr.data);
    } catch (e) {
      console.warn('Analytics load error:', e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (period === 'customRange') return;
    const days = periodDays[period] || 30;
    loadAnalyticsData({ days });
  }, [period]);

  const handleCustomRangeApply = () => {
    if (!customStart || !customEnd) return;
    setPeriod('customRange');
    loadAnalyticsData({ startDate: customStart, endDate: customEnd });
  };

  const chartColors = {
    grid: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    text: theme === 'dark' ? '#8892a4' : '#546e7a',
    tooltip: { backgroundColor: 'rgba(255,255,255,0.96)', titleColor: '#2c3e50', bodyColor: '#546e7a', borderColor: '#e1e4e8', borderWidth: 1, padding: 12 },
  };

  // ── KPI summary cards from real data ──────────────────────────────────────
  const kpiEntries = kpiData ? Object.entries(kpiData) : [];
  const totalRevenue = kpiEntries.reduce((s, [, v]) => s + (v?.total || 0), 0);
  const totalTxn     = kpiEntries.reduce((s, [, v]) => s + (v?.transactions || 0), 0);
  const topEntry     = [...kpiEntries].sort((a, b) => (b[1]?.total || 0) - (a[1]?.total || 0))[0];
  const topModKey    = topEntry?.[0];
  const topModLabel  = topModKey ? t(topModKey) : '-';

  // Growth: compare last half vs first half of trend
  const growthCalc = (() => {
    if (trendByModule.length < 4) return { pct: 0, up: true };
    const half = Math.floor(trendByModule.length / 2);
    const sumHalf = (arr) => arr.reduce((s, d) => s + (d.toll||0) + (d.bridge||0) + (d.wim||0) + (d.auction||0) + (d.topup||0), 0);
    const recent = sumHalf(trendByModule.slice(-half));
    const prev   = sumHalf(trendByModule.slice(0, half));
    if (prev <= 0) return { pct: 0, up: true };
    const pct = ((recent - prev) / prev) * 100;
    return { pct, up: pct >= 0 };
  })();

  const summaryKpis = [
    { label: t('totalRevenue'), val: formatKip(totalRevenue), sub: period === 'customRange' ? `${customStart} ~ ${customEnd}` : `${periodDays[period] || 30} days`, color: '#1890ff' },
    { label: t('totalTxn'),     val: totalTxn.toLocaleString(),                sub: 'transactions',               color: '#52c41a' },
    { label: t('growthRate'),   val: `${growthCalc.up ? '+' : ''}${growthCalc.pct.toFixed(1)}%`, sub: 'vs prev period', color: '#9254de' },
    { label: t('topSource'),    val: topModLabel, sub: topEntry ? `${((topEntry[1]?.total||0)/Math.max(1,totalRevenue)*100).toFixed(1)}% share` : '-', color: topModKey ? MODULE_COLORS[topModKey] : '#fa8c16' },
  ];

  // ── Trend chart (multi-line by module) ────────────────────────────────────
  const trendChartData = {
    labels: trendByModule.map(d => {
      const parts = d.date.split('-');
      return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d.date;
    }),
    datasets: [
      { label: t('toll'),    data: trendByModule.map(d => d.toll    || 0), borderColor: '#fa8c16', backgroundColor: 'rgba(250,140,22,0.08)', fill: true, tension: 0.4, pointRadius: trendByModule.length > 30 ? 0 : 2, borderWidth: 2 },
      { label: t('bridge'),  data: trendByModule.map(d => d.bridge  || 0), borderColor: '#1890ff', backgroundColor: 'rgba(24,144,255,0.08)',  fill: true, tension: 0.4, pointRadius: trendByModule.length > 30 ? 0 : 2, borderWidth: 2 },
      { label: t('wim'),     data: trendByModule.map(d => d.wim     || 0), borderColor: '#f5222d', backgroundColor: 'rgba(245,34,45,0.06)',   fill: true, tension: 0.4, pointRadius: trendByModule.length > 30 ? 0 : 2, borderWidth: 2 },
      { label: t('auction'), data: trendByModule.map(d => d.auction || 0), borderColor: '#52c41a', backgroundColor: 'rgba(82,196,26,0.06)',   fill: true, tension: 0.4, pointRadius: trendByModule.length > 30 ? 0 : 2, borderWidth: 2 },
      { label: t('topup'),   data: trendByModule.map(d => d.topup   || 0), borderColor: '#9254de', backgroundColor: 'rgba(146,84,222,0.06)',   fill: true, tension: 0.4, pointRadius: trendByModule.length > 30 ? 0 : 2, borderWidth: 2 },
    ],
  };
  const trendOptions = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, position: 'top', labels: { color: chartColors.text, font: { size: 11 }, usePointStyle: true, pointStyleWidth: 8, padding: 16 } },
      tooltip: chartColors.tooltip,
    },
    scales: {
      x: { grid: { color: chartColors.grid }, ticks: { color: chartColors.text, font: { size: 10 }, maxTicksLimit: 10 } },
      y: { grid: { color: chartColors.grid }, ticks: { color: chartColors.text, font: { size: 11 }, callback: v => `₭${v}B` } },
    },
  };

  // ── Hourly distribution chart ─────────────────────────────────────────────
  const maxHourly = Math.max(1, ...hourlyData.map(h => h.count));
  const hourlyChartData = {
    labels: hourlyData.map(h => h.hour),
    datasets: [{
      label: t('txnVolume'),
      data: hourlyData.map(h => h.count),
      backgroundColor: hourlyData.map(h => h.count === maxHourly ? 'rgba(24,144,255,0.9)' : 'rgba(24,144,255,0.4)'),
      borderRadius: 4,
      borderSkipped: false,
    }],
  };
  const hourlyOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { ...chartColors.tooltip, callbacks: { label: v => `${v.raw.toLocaleString()} transactions` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: chartColors.text, font: { size: 9 }, maxTicksLimit: 12 } },
      y: { grid: { color: chartColors.grid }, ticks: { color: chartColors.text, font: { size: 11 } } },
    },
  };

  // ── Module share doughnut ─────────────────────────────────────────────────
  const srcLabel = (s) => lang === 'lo' ? (s.name_la || s.name_en) : (s.name_en || s.name_la);
  const moduleShareData = {
    labels: sourceData.map(srcLabel),
    datasets: [{
      data: sourceData.map(s => s.percentage),
      backgroundColor: sourceData.map(s => MODULE_COLORS[s.key || s.module] || '#888'),
      borderWidth: 0,
      hoverOffset: 6,
    }],
  };
  const moduleShareOptions = {
    responsive: true, maintainAspectRatio: false, cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: { ...chartColors.tooltip, callbacks: { label: v => `${v.label}: ${v.raw}%` } },
    },
  };

  // ── System health from api_status ─────────────────────────────────────────
  const totalServices  = apiStatus.length || 1;
  const okServices     = apiStatus.filter(s => s.status === 'OK').length;
  const lagServices    = apiStatus.filter(s => s.status === 'LAG').length;
  const avgResponseMs  = apiStatus.length
    ? Math.round(apiStatus.reduce((s, a) => s + (a.responseTime || 0), 0) / apiStatus.length)
    : 0;

  const healthMetrics = [
    { label: t('uptime'),      value: `${((okServices / totalServices) * 100).toFixed(1)}%`, color: okServices === totalServices ? '#52c41a' : '#fa8c16', good: okServices === totalServices, detail: `${okServices}/${totalServices} services OK` },
    { label: t('errorRate'),   value: `${((lagServices / totalServices) * 100).toFixed(1)}%`, color: lagServices === 0 ? '#52c41a' : '#f5222d', good: lagServices === 0, detail: lagServices === 0 ? '< 1% threshold' : `${lagServices} service(s) lagging` },
    { label: t('avgResponse'), value: `${avgResponseMs}ms`, color: avgResponseMs < 200 ? '#1890ff' : '#fa8c16', good: avgResponseMs < 500, detail: 'API avg response' },
    { label: t('activeGates'), value: `${okServices}/${totalServices}`, color: okServices === totalServices ? '#52c41a' : '#fa8c16', good: okServices === totalServices, detail: lagServices > 0 ? `${lagServices} gate(s) lagging` : 'All gates OK' },
  ];

  return (
    <div className="main">
      {/* Header */}
      <div className="section-header" style={{ flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div className="section-title">{t('title')}</div>
          <div className="section-sub">{t('subtitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {['today', 'week7', '30d', '90d'].map((p) => (
            <div key={p} className={`filter-chip ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
              {t(p)}
            </div>
          ))}
          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 8px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('fromDate')}:</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('toDate')}:</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleCustomRangeApply}
              disabled={!customStart || !customEnd}
              style={{
                background: 'var(--blue)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '5px 12px',
                fontSize: '12px',
                cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer',
                opacity: (!customStart || !customEnd) ? 0.5 : 1,
                fontFamily: 'var(--font)',
                fontWeight: '600',
                transition: 'all 0.2s ease',
              }}
            >
              {t('apply')}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="kpi-grid">
            {summaryKpis.map((k) => (
              <div key={k.label} className="kpi">
                <div className="kpi-accent" style={{ background: k.color }} />
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value" style={{ color: k.color }}>{k.val}</div>
                <div className="kpi-sub kpi-up">↑ {k.sub}</div>
              </div>
            ))}
          </div>

          {/* Revenue Trend Chart */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-title">{t('revTrend')}</div>
            <div className="card-sub">{t('moduleComp')} — ₭ Billion</div>
            <div style={{ height: '240px', marginTop: '12px' }}>
              <Line data={trendChartData} options={trendOptions} />
            </div>
          </div>

          {/* Hourly + Module Share */}
          <div className="chart-row">
            <div className="card">
              <div className="card-title">{t('hourlyDist')}</div>
              <div className="card-sub">{t('txnVolume')} — {new Date().toLocaleDateString()}</div>
              <div style={{ height: '200px', marginTop: '12px' }}>
                <Bar data={hourlyChartData} options={hourlyOptions} />
              </div>
            </div>
            <div className="card">
              <div className="card-title">{t('moduleComp')}</div>
              <div className="card-sub">% {lang === 'lo' ? 'ສ່ວນແບ່ງ' : lang === 'th' ? 'สัดส่วน' : 'Share of total'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'center', marginTop: '12px' }}>
                <div style={{ height: '160px' }}>
                  <Doughnut data={moduleShareData} options={moduleShareOptions} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {sourceData.map(s => (
                    <div key={s.key || s.module} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: MODULE_COLORS[s.key || s.module] || '#888', flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text)' }}>{srcLabel(s)}</div>
                        <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{formatKip(s.amount)}</div>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: MODULE_COLORS[s.key || s.module] || '#888' }}>{s.percentage}%</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Top Stations + System Health */}
          <div className="chart-row">
            <div className="card">
              <div className="card-title">{t('topPerf')}</div>
              <div className="card-sub">{lang === 'lo' ? 'ລາຍຮັບສູງສຸດ ໃນໄລຍະ' : lang === 'th' ? 'รายได้สูงสุดในช่วง' : 'Highest revenue in period'}</div>
              <table className="data-table" style={{ marginTop: '12px' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>{t('rank')}</th>
                    <th>{t('station')}</th>
                    <th style={{ textAlign: 'right' }}>{t('revenue')}</th>
                    <th style={{ textAlign: 'right' }}>{t('change')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topStations.map((s) => {
                    const totalTop = topStations.reduce((sum, x) => sum + x.revenue, 0);
                    const sharePct = totalTop > 0 ? ((s.revenue / totalTop) * 100).toFixed(1) : '0';
                    return (
                      <tr key={s.rank}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', fontSize: '10px', fontWeight: '700', background: s.rank <= 3 ? MODULE_COLORS[s.module] : 'var(--bg4)', color: s.rank <= 3 ? '#fff' : 'var(--text3)' }}>
                            {s.rank}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: MODULE_COLORS[s.module] || '#888', flexShrink: 0 }} />
                            <span style={{ fontSize: '12px' }}>{lang === 'lo' ? s.name_la : s.name_en}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: '600', fontFamily: 'var(--mono)', fontSize: '12px' }}>
                          {formatKip(s.revenue)}
                        </td>
                        <td style={{ textAlign: 'right', color: MODULE_COLORS[s.module] || '#888', fontWeight: '600', fontSize: '12px' }}>
                          {sharePct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* System Health */}
            <div className="card">
              <div className="card-title">{t('systemHealth')}</div>
              <div className="card-sub">{t('systemPerf')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                {healthMetrics.map((m) => (
                  <div key={m.label} style={{ padding: '16px', background: 'var(--bg3)', borderRadius: '8px', border: `1px solid ${m.color}30` }}>
                    <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '8px' }}>{m.label}</div>
                    <div style={{ fontSize: '22px', fontWeight: '700', color: m.color, fontFamily: 'var(--mono)', marginBottom: '4px' }}>{m.value}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{m.detail}</div>
                    <div style={{ marginTop: '10px', height: '3px', borderRadius: '2px', background: 'var(--bg4)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: '2px', background: m.color, width: m.good ? '97%' : '60%', transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Live API status list */}
              <div style={{ marginTop: '16px' }}>
                {apiStatus.map((svc) => {
                  const ok = svc.status === 'OK';
                  return (
                    <div key={svc.name || svc.service_name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '12px' }}>
                      <div style={{ width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0, background: ok ? '#52c41a' : '#fa8c16', boxShadow: `0 0 6px ${ok ? '#52c41a' : '#fa8c16'}80` }} />
                      <span style={{ flex: 1, color: 'var(--text2)' }}>{svc.name || svc.service_name}</span>
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: ok ? '#52c41a' : '#fa8c16' }}>{svc.responseTime || svc.response_time}ms</span>
                      <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', background: ok ? 'rgba(82,196,26,.1)' : 'rgba(250,140,22,.1)', color: ok ? '#52c41a' : '#fa8c16' }}>
                        {svc.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* ── Revenue Breakdown by Type ─────────────────────────────── */}
          {(() => {
            const MODULE_DEF = [
              { key: 'toll',    icon: '◆', label: { lo: 'ຄ່າທາງດ່ວນ',    th: 'ค่าทางด่วน',      en: 'Toll Fee' },       color: '#fa8c16', bg: 'rgba(250,140,22,.12)' },
              { key: 'bridge',  icon: '◈', label: { lo: 'ຄ່າຂ້າມສະພານ', th: 'ค่าข้ามสะพาน',   en: 'Bridge Fee' },     color: '#1890ff', bg: 'rgba(24,144,255,.12)' },
              { key: 'wim',     icon: '⬡', label: { lo: 'ຄ່າປັບນ້ຳໜັກ', th: 'ค่าปรับน้ำหนัก', en: 'WIM Fine' },       color: '#f5222d', bg: 'rgba(245,34,45,.12)'  },
              { key: 'auction', icon: '◆', label: { lo: 'ປະມູນປ້າຍ',    th: 'ประมูลป้าย',      en: 'Plate Auction' }, color: '#52c41a', bg: 'rgba(82,196,26,.12)'  },
            ];

            const kpiRows = MODULE_DEF.map(m => {
              const v = kpiData?.[m.key] || {};
              return { ...m, total: v.total || 0, txn: v.transactions || 0, avg: v.average || 0 };
            });

            const grandTotal = kpiRows.reduce((s, r) => s + r.total, 0);
            const grandTxn   = kpiRows.reduce((s, r) => s + r.txn, 0);

            const colRevenue     = lang === 'lo' ? 'ລາຍຮັບລວມ'       : lang === 'th' ? 'รายได้รวม'       : 'Total Revenue';
            const colTxn         = lang === 'lo' ? 'ທຸລະກຳ'           : lang === 'th' ? 'ธุรกรรม'         : 'Transactions';
            const colAvg         = lang === 'lo' ? 'ສະເລ່ຍ/ທຸລະກຳ'   : lang === 'th' ? 'เฉลี่ย/ธุรกรรม' : 'Avg / Txn';
            const colShare       = lang === 'lo' ? 'ສ່ວນແບ່ງ'         : lang === 'th' ? 'สัดส่วน'         : 'Share';
            const colType        = lang === 'lo' ? 'ປະເພດລາຍຮັບ'      : lang === 'th' ? 'ประเภทรายได้'    : 'Revenue Type';
            const titleBreakdown = lang === 'lo' ? 'ສະຫຼຸບລາຍຮັບຕາມປະເພດ' : lang === 'th' ? 'สรุปรายได้ตามประเภท' : 'Revenue Breakdown by Type';
            const subBreakdown   = lang === 'lo' ? 'ລາຍຮັບທຸກໂມດູນ ໃນໄລຍະທີ່ເລືອກ' : lang === 'th' ? 'รายได้ทุกโมดูลในช่วงที่เลือก' : 'All modules in selected period';
            const totalLabel     = lang === 'lo' ? 'ລວມທັງໝົດ' : lang === 'th' ? 'รวมทั้งหมด' : 'Grand Total';

            return (
              <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: '0' }}>
                {/* Card header */}
                <div style={{ padding: '16px 20px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div className="card-title">{titleBreakdown}</div>
                    <div className="card-sub" style={{ marginTop: '2px' }}>{subBreakdown}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {kpiRows.map(m => (
                      <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '3px 10px', borderRadius: '20px', background: m.bg, fontSize: '11px', fontWeight: '600', color: m.color }}>
                        <span>{m.icon}</span>
                        <span>{m.label[lang] || m.label.en}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary mini-cards row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0', borderBottom: '1px solid var(--border)' }}>
                  {kpiRows.map((m, i) => {
                    const sharePct = grandTotal > 0 ? (m.total / grandTotal * 100) : 0;
                    return (
                      <div key={m.key} style={{ padding: '16px 20px', borderRight: i < 3 ? '1px solid var(--border)' : 'none', position: 'relative', overflow: 'hidden' }}>
                        {/* color bar top */}
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: m.color }} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: m.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: m.color }}>
                            {m.icon}
                          </div>
                          <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text2)' }}>{m.label[lang] || m.label.en}</span>
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: m.color, fontFamily: 'var(--mono)', lineHeight: 1.1 }}>
                          {formatKip(m.total)}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '4px' }}>
                          {m.txn.toLocaleString()} txn
                        </div>
                        {/* share bar */}
                        <div style={{ marginTop: '10px', height: '4px', borderRadius: '2px', background: 'var(--bg4)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '2px', background: m.color, width: `${sharePct}%`, transition: 'width 0.8s ease' }} />
                        </div>
                        <div style={{ fontSize: '10px', color: m.color, fontWeight: '600', marginTop: '4px' }}>{sharePct.toFixed(1)}%</div>
                      </div>
                    );
                  })}
                </div>

                {/* Detail table */}
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '36px' }}>#</th>
                      <th>{colType}</th>
                      <th style={{ textAlign: 'right' }}>{colRevenue}</th>
                      <th style={{ textAlign: 'right' }}>{colTxn}</th>
                      <th style={{ textAlign: 'right' }}>{colAvg}</th>
                      <th style={{ textAlign: 'right', width: '70px' }}>{colShare}</th>
                      <th style={{ width: '160px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {kpiRows.map((m, i) => {
                      const sharePct = grandTotal > 0 ? (m.total / grandTotal * 100) : 0;
                      return (
                        <tr key={m.key}>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', fontSize: '10px', fontWeight: '700', background: m.bg, color: m.color }}>
                              {i + 1}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: m.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: m.color, flexShrink: 0 }}>
                                {m.icon}
                              </div>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{m.label[lang] || m.label.en}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase' }}>{m.key}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: '700', fontSize: '14px', color: m.color }}>
                            {formatKip(m.total)}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '13px' }}>
                            {m.txn.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px', color: 'var(--text3)' }}>
                            ₭ {m.avg.toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: '700', fontSize: '13px', color: m.color }}>
                            {sharePct.toFixed(1)}%
                          </td>
                          <td>
                            <div style={{ height: '6px', borderRadius: '3px', background: 'var(--bg4)', overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: '3px', background: m.color, width: `${sharePct}%`, transition: 'width 0.8s ease' }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Grand total footer */}
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)' }}>
                      <td colSpan={2} style={{ padding: '12px 16px', fontWeight: '700', fontSize: '13px', color: 'var(--text)' }}>
                        {totalLabel}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: '700', fontSize: '14px', color: 'var(--text)', padding: '12px 16px' }}>
                        {formatKip(grandTotal)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontWeight: '700', fontSize: '13px', color: 'var(--text)', padding: '12px 16px' }}>
                        {grandTxn.toLocaleString()}
                      </td>
                      <td colSpan={3} style={{ padding: '12px 16px' }} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default Analytics;